import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  CellularBillingPreview,
  BillingWorkflow,
  BillingPeriodState,
  BillingOverageRate, BillingActivePartner, OverlayPriceQuote,
  OverlayBillingRow,
  OverlayBundleRecord,
  CellularBillingReview,
  CellularBillingReport,
  CellularBillingRow,
  CellularPoolingSnapshot, ReconciliationDraft
} from '../model/cellular-billing';

@Injectable({ providedIn: 'root' })
export class CellularBillingService {
  readonly configurationChanged = new Subject<void>();
  private readonly endpoint = environment.endpoints.cellularBilling;

  constructor(private http: HttpClient) { }

  createPreview(
    month: number,
    year: number,
    license: File,
    teal: File,
    kore: File
  ): Observable<CellularBillingPreview> {
    const formData = new FormData();
    formData.append('month', String(month));
    formData.append('year', String(year));
    formData.append('license', license, license.name);
    formData.append('teal', teal, teal.name);
    formData.append('kore', kore, kore.name);
    return this.http.post<{ data: CellularBillingPreview }>(`${this.endpoint}/previews`, formData)
      .pipe(map(response => response.data));
  }

  saveReport(
    month: number,
    year: number,
    thresholdGb: number,
    rows: CellularBillingRow[],
    overlayRows?: OverlayBillingRow[],
    overlayThresholdGb = 14,
    pooling?: CellularPoolingSnapshot,
    reconciliation?: ReconciliationDraft
  ): Observable<CellularBillingReport> {
    return this.http.post<{ data: CellularBillingReport }>(`${this.endpoint}/reports`, {
      month,
      year,
      thresholdGb,
      rows,
      ...(reconciliation ? { reconciliationId: reconciliation._id, reconciliationRevision: reconciliation.revision } : {}),
      ...(pooling && [2, 3].includes(pooling.version) ? { poolingRoster: pooling.roster } : {}),
      ...(pooling && pooling.version === 3 ? { poolingPolicies: pooling.policies || [] } : {}),
      ...(overlayRows ? { overlayRows, overlayThresholdGb, includePricingSnapshot: true } : {})
    }).pipe(map(response => response.data));
  }

  listReports(month: number, year: number): Observable<CellularBillingReport[]> {
    const params = new HttpParams()
      .set('month', String(month))
      .set('year', String(year));
    return this.http.get<{ data: CellularBillingReport[] }>(`${this.endpoint}/reports`, { params })
      .pipe(map(response => response.data || []));
  }
  getReconciliation(id: string): Observable<ReconciliationDraft> {
    return this.http.get<{ data: ReconciliationDraft }>(`${this.endpoint}/previews/${encodeURIComponent(id)}/reconciliation`).pipe(map(response => response.data));
  }
  changeReconciliation(id: string, body: object): Observable<ReconciliationDraft> {
    return this.http.post<{ data: ReconciliationDraft }>(`${this.endpoint}/previews/${encodeURIComponent(id)}/reconciliation`, body).pipe(map(response => response.data));
  }
  listReconciliations(month: number, year: number): Observable<Partial<ReconciliationDraft>[]> {
    const params = new HttpParams().set('month', String(month)).set('year', String(year));
    return this.http.get<{ data: Partial<ReconciliationDraft>[] }>(`${this.endpoint}/previews/reconciliations`, { params }).pipe(map(response => response.data || []));
  }
  getReportPeriod(month: number, year: number): Observable<{ reports: CellularBillingReport[], period: BillingPeriodState }> {
    const params = new HttpParams().set('month', String(month)).set('year', String(year));
    return this.http.get<{ data: CellularBillingReport[], period: BillingPeriodState }>(`${this.endpoint}/reports`, { params })
      .pipe(map(response => ({ reports: response.data || [], period: response.period })));
  }

  getReport(reportId: string): Observable<CellularBillingReport> {
    return this.http.get<{ data: CellularBillingReport }>(`${this.endpoint}/reports/${reportId}`)
      .pipe(map(response => response.data));
  }
  getWorkflow(reportId: string): Observable<BillingWorkflow> {
    return this.http.get<{ data: BillingWorkflow }>(`${this.endpoint}/reports/${reportId}/workflow`).pipe(map(response => response.data));
  }
  changeWorkflow(reportId: string, revision: number, action: string, report: CellularBillingReport, comment: string, portalUrl: string): Observable<BillingWorkflow> {
    return this.http.post<{ data: BillingWorkflow }>(`${this.endpoint}/reports/${reportId}/workflow`, {
      revision, action, rows: report.rows || [], overlayRows: report.overlayRows, comment, portalUrl,
      ...(report.pooling && [2, 3].includes(report.pooling.version) ? { poolingRoster: report.pooling.roster } : {}),
      ...(report.pooling && report.pooling.version === 3 ? { poolingVersion: 3, poolingPolicies: report.pooling.policies || [] } : {})
    }).pipe(map(response => response.data));
  }
  retryWorkflowEmail(reportId: string, revision: number, portalUrl: string): Observable<BillingWorkflow> {
    return this.http.post<{ data: BillingWorkflow }>(`${this.endpoint}/reports/${reportId}/workflow/email`, { revision, portalUrl }).pipe(map(response => response.data));
  }
  exportWorkflow(reportId: string): Observable<Blob> {
    return this.http.get(`${this.endpoint}/reports/${reportId}/workflow/export`, { responseType: 'blob' });
  }

  exportReport(reportId: string): Observable<Blob> {
    return this.http.get(`${this.endpoint}/reports/${reportId}/export`, { responseType: 'blob' });
  }

  requestReview(reportId: string, portalUrl: string): Observable<any> {
    return this.http.post(`${this.endpoint}/reports/${reportId}/review-email`, { portalUrl });
  }

  getReview(reportId: string): Observable<CellularBillingReview> {
    return this.http.get<{ data: CellularBillingReview }>(`${this.endpoint}/reports/${reportId}/review`)
      .pipe(map(response => response.data));
  }

  decideReview(reportId: string, status: string, comment: string, portalUrl: string): Observable<CellularBillingReview> {
    return this.http.post<{ data: CellularBillingReview }>(`${this.endpoint}/reports/${reportId}/review-decision`, { status, comment, portalUrl })
      .pipe(map(response => response.data));
  }

  retryRevisionEmail(reportId: string, portalUrl: string): Observable<CellularBillingReview> {
    return this.http.post<{ data: CellularBillingReview }>(`${this.endpoint}/reports/${reportId}/revision-email`, { portalUrl })
      .pipe(map(response => response.data));
  }

  listOverlayBundles(): Observable<OverlayBundleRecord[]> {
    return this.http.get<{data: OverlayBundleRecord[]}>(`${this.endpoint}/overlay-bundles`).pipe(map(response => response.data));
  }

  createOverlayBundle(input: any): Observable<OverlayBundleRecord> {
    return this.http.post<{data: OverlayBundleRecord}>(`${this.endpoint}/overlay-bundles`, input).pipe(map(response => response.data));
  }
  updateConfiguration(kind: 'overlay-bundles' | 'data-overages' | 'active-partners', id: string, revision: number, input: any): Observable<any> {
    return this.http.put(`${this.endpoint}/${kind}/${encodeURIComponent(id)}`, { ...input, revision });
  }
  deleteConfiguration(kind: 'overlay-bundles' | 'data-overages' | 'active-partners', id: string, revision: number): Observable<any> {
    return this.http.delete(`${this.endpoint}/${kind}/${encodeURIComponent(id)}`, { params: new HttpParams().set('revision', String(revision)) });
  }

  mapOverlay(rows: OverlayBillingRow[], month: number, year: number): Observable<OverlayBillingRow[]> {
    return this.http.post<{data: OverlayBillingRow[]}>(`${this.endpoint}/overlay-bundles/map`, { rows, month, year }).pipe(map(response => response.data));
  }
  listDataOverages(): Observable<BillingOverageRate[]> {
    return this.http.get<{data: BillingOverageRate[]}>(`${this.endpoint}/data-overages`).pipe(map(response => response.data));
  }
  createDataOverage(input: any): Observable<any> { return this.http.post(`${this.endpoint}/data-overages`, input); }
  importSuppliedPriceCard(): Observable<any> { return this.http.post(`${this.endpoint}/data-overages/import-price-card`, {}); }
  listActivePartners(): Observable<BillingActivePartner[]> {
    return this.http.get<{data: BillingActivePartner[]}>(`${this.endpoint}/active-partners`).pipe(map(response => response.data));
  }
  createActivePartner(input: any): Observable<any> { return this.http.post(`${this.endpoint}/active-partners`, input); }
  importActivePartners(file: File): Observable<any> {
    const form = new FormData(); form.append('partners', file, file.name);
    return this.http.post(`${this.endpoint}/active-partners/import`, form);
  }
  quoteOverlay(partnerName: string, dataUsageGb: number, bundle: string, bundleGb: number): Observable<OverlayPriceQuote> {
    return this.http.post<{data: OverlayPriceQuote}>(`${this.endpoint}/overlay-pricing/quote`, { partnerName, dataUsageGb, bundle, bundleGb }).pipe(map(response => response.data));
  }
  quoteOverlayRows(rows: OverlayBillingRow[]): Observable<{ rowId: string, pricing: OverlayPriceQuote }[]> {
    return this.http.post<{data: { rowId: string, pricing: OverlayPriceQuote }[]}>(`${this.endpoint}/overlay-pricing/quote`, {
      rows: rows.map(row => ({ rowId: row.rowId, partnerName: row.msp, dataUsageGb: row.dataUsageGb, bundle: row.bundle, bundleGb: row.bundleGb, bundleMapping: row.bundleMapping }))
    }).pipe(map(response => response.data));
  }
}
